import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { UserStore } from 'app/core/services/user.store'; // Adjust import path as needed
import { catchError, Observable, throwError } from 'rxjs';
import { UtilityService } from '../../shared/services/utility.service';

/**
 * Marks a request as "always run as the real logged-in user".
 *
 * Admin tooling that stays available during an impersonation (action watcher)
 * must not be sent as the impersonated user: a 403 on such a call would cancel
 * the whole takeover.
 */
export const SKIP_IMPERSONATION = new HttpContextToken(() => false);

/**
 * Impersonation interceptor
 * Adds X-Selected-User header to requests when a user is being impersonated
 *
 * @param req The original HTTP request
 * @param next The next handler in the interceptor chain
 */
export const impersonationInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const userStore = inject(UserStore);
  const utilityService = inject(UtilityService);

  if (userStore.impersonationData() && !req.context.get(SKIP_IMPERSONATION)) {
    const impersonatedReq = req.clone({
      headers: req.headers.set('X-Selected-User', userStore.selectedUserId() || ''),
    });

    return next(impersonatedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error?.status === 403 && error?.error?.detail === 'You do not have access to this resource') {
          userStore.stopImpersonating();
          utilityService.showMessage('Je heb geen rechten om deze persoon over te nemen. ' + error?.error?.detail);
        }
        return throwError(() => error);
      })
    );
  }

  return next(req);
};
